import { cliSimpleGit } from '../../cli-simple-git.js';
import { normalizePatchContent } from '@buildautomaton/types';
import type { WorkingTreeChangeKind } from '../types.js';
import { truncatePatch } from './patch-truncate.js';

const RENAME_DIFF_ARGS = ['-M', '--find-renames'] as const;

function devNullPath(): string {
  return process.platform === 'win32' ? 'NUL' : '/dev/null';
}

export function patchTextFromGitDiffOutput(raw: string): string | undefined {
  if (raw.trim() === '') return undefined;
  return normalizePatchContent(raw);
}

async function rawGitDiff(g: ReturnType<typeof cliSimpleGit>, args: readonly string[]): Promise<string> {
  return String(await g.raw([...args]).catch(() => ''));
}

async function unifiedDiffAgainstHead(
  g: ReturnType<typeof cliSimpleGit>,
  pathInRepo: string,
  change: WorkingTreeChangeKind,
  movedFromPathInRepo?: string,
): Promise<string | undefined> {
  const args =
    change === 'moved' && movedFromPathInRepo
      ? (['diff', '--no-color', ...RENAME_DIFF_ARGS, 'HEAD', '--', movedFromPathInRepo, pathInRepo] as const)
      : change === 'removed'
        ? (['diff', '--no-color', ...RENAME_DIFF_ARGS, 'HEAD', '--', pathInRepo] as const)
        : (['diff', '--no-color', ...RENAME_DIFF_ARGS, 'HEAD', '--', pathInRepo] as const);
  return patchTextFromGitDiffOutput(await rawGitDiff(g, args));
}

/** Untracked files are absent from `git diff HEAD`; compare against `/dev/null` instead. */
async function unifiedDiffForAddedUntrackedFile(
  g: ReturnType<typeof cliSimpleGit>,
  pathInRepo: string,
): Promise<string | undefined> {
  const raw = await rawGitDiff(g, [
    'diff',
    '--no-color',
    ...RENAME_DIFF_ARGS,
    '--no-index',
    '--',
    devNullPath(),
    pathInRepo,
  ]);
  return patchTextFromGitDiffOutput(raw);
}

export async function unifiedDiffForFile(
  repoCwd: string,
  pathInRepo: string,
  change: WorkingTreeChangeKind,
  movedFromPathInRepo?: string,
): Promise<string | undefined> {
  const g = cliSimpleGit(repoCwd);
  let patch = await unifiedDiffAgainstHead(g, pathInRepo, change, movedFromPathInRepo);
  if (!patch && change === 'added') {
    patch = await unifiedDiffForAddedUntrackedFile(g, pathInRepo);
  }
  return patch ? truncatePatch(patch) : undefined;
}

/** Diff for a single commit range (parent..sha). */
export async function unifiedDiffForFileInRange(
  repoCwd: string,
  range: string,
  pathInRepo: string,
  change: WorkingTreeChangeKind,
  movedFromPathInRepo?: string,
): Promise<string | undefined> {
  const g = cliSimpleGit(repoCwd);
  const args =
    change === 'moved' && movedFromPathInRepo
      ? (['diff', '--no-color', ...RENAME_DIFF_ARGS, '-U20000', range, '--', movedFromPathInRepo, pathInRepo] as const)
      : (['diff', '--no-color', ...RENAME_DIFF_ARGS, '-U20000', range, '--', pathInRepo] as const);
  const patch = patchTextFromGitDiffOutput(await rawGitDiff(g, args));
  return patch ? truncatePatch(patch) : undefined;
}
